import { useCallback, useRef } from "react";
import type { RefObject } from "react";
import { pilotNameSchema } from "@frontier/protocol/auth-contracts";
import type { Profile } from "@frontier/protocol/contracts";
import type { ProfileSync } from "./profile-sync";
import { ApiError, api } from "./session-api";

interface ProfileRoom {
  send(type: "syncProfile", message: Record<string, never>): void;
}

export function usePilotName(
  profile: Profile | null,
  token: RefObject<string>,
  sync: ProfileSync,
  room: RefObject<ProfileRoom | null>,
  busy: boolean,
  setBusy: (busy: boolean) => void,
  setNotice: (message: string) => void,
  unauthorized: (message: string) => void,
  observeNickname: (token: string, profile: Profile) => void,
): (name: string) => Promise<boolean> {
  const active = useRef({ profile, busy });
  active.current = { profile, busy };
  const pending = useRef<{ ticket: number; token: string } | null>(null);
  return useCallback(
    async (name: string) => {
      const currentProfile = active.current.profile;
      const ticket = sync.ticket();
      const sessionToken = token.current;
      if (
        !currentProfile ||
        !sessionToken ||
        active.current.busy ||
        (pending.current?.ticket === ticket &&
          pending.current.token === sessionToken)
      )
        return false;
      const parsed = pilotNameSchema.safeParse(name);
      if (!parsed.success)
        throw new Error(
          "Ник: от 1 до 24 символов.",
        );
      if (parsed.data === currentProfile.name) return true;
      const operation = { ticket, token: sessionToken };
      pending.current = operation;
      const current = () =>
        sync.ticket() === ticket &&
        token.current === sessionToken &&
        active.current.profile?.playerId === currentProfile.playerId;
      setBusy(true);
      try {
        const updated = await api.renameProfile(sessionToken, parsed.data);
        if (!current() || !sync.commit(updated, ticket)) return false;
        observeNickname(sessionToken, updated);
        try {
          room.current?.send("syncProfile", {});
          setNotice("Ник сохранён.");
        } catch (error) {
          console.warn("pilot-name-world-sync-failed", {
            playerId: currentProfile.playerId,
            message:
              error instanceof Error
                ? error.message
                : "World connection unavailable",
          });
          setNotice("Ник сохранён. Имя в секторе обновится после подключения.");
        }
        return true;
      } catch (error) {
        if (!current()) return false;
        if (error instanceof ApiError && error.status === 401)
          unauthorized("Сессия истекла. Войди снова.");
        throw error;
      } finally {
        if (pending.current === operation) pending.current = null;
        if (current()) setBusy(false);
      }
    },
    [token, sync, room, setBusy, setNotice, unauthorized, observeNickname],
  );
}
